import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Subject } from 'rxjs';
import { CardpageBaseComponent } from '../../abstract/cardpage-base/cardpage-base.component';
import { PatientHeaderComponent } from '../../components/patient-header/patient-header.component';
import { PatientRowComponent } from '../../components/patient-row/patient-row.component';
import { UserDialogComponent } from '../../dialogs/user-dialog/user-dialog.component';
import { DashboardClinic } from '../../models/dashboard';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Parameters } from '../../utilities/parameters';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-dashboard-clinic',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    CardpageBaseComponent,
    CardModule,
    ButtonModule,
    PanelModule,
    TableModule,
    TagModule,
    UserDialogComponent,
    DividerModule,
    PatientHeaderComponent,
    PatientRowComponent,
    TranslatePipe
  ],
  templateUrl: './dashboard-clinic.component.html',
  styles: ``,
})
export class DashboardClinicComponent implements OnInit, OnDestroy {
  messageService = inject(UiNotificationService);
  routerService = inject(Router);

  private translate = inject(TranslateService);

  loading: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  dashboard: DashboardClinic = Reset.dashboardClinic();

  showDialogUser: boolean = false;
  newUser: User = Reset.user();

  constructor(private apiService: ApiService) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy() {
    this.dtTrigger.unsubscribe();
  }

  // 1. Definiamo le chiavi di traduzione associate alle fasce orarie
  private readonly greetingKeys = [
    'greetings.morning',
    'greetings.afternoon',
    'greetings.evening',
    'greetings.night'
  ];

  // 2. Creiamo un signal reattivo per le traduzioni correnti
  private currentTranslations = toSignal(this.translate.stream(this.greetingKeys), { initialValue: {} as Record<string, string> });

  // 3. Il tuo metodo calcola ora solo la CHIAVE corretta (operazione sincrona e velocissima)
  private getGreetingKey(): string {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) return 'greetings.morning';
    if (hour >= 12 && hour < 18) return 'greetings.afternoon';
    if (hour >= 18 && hour < 22) return 'greetings.evening';
    return 'greetings.night';
  }

  // 4. Espone il titolo finale come Computed Signal pronto per il template
  readonly getTitle = computed<string>(() => {
    const translations = this.currentTranslations();
    const activeKey = this.getGreetingKey();

    // Recupera la stringa tradotta (o usa una stringa vuota temporanea di fallback)
    const baseGreeting = translations[activeKey] || '';

    // Gestione del nome utente
    const userName = Parameters.loggedUser.id !== '' ? Parameters.loggedUser.name : '';

    // Interpoliamo manualmente i parametri all'interno del Computed Signal
    // Ngx-translate usa le doppie graffe: {{name}}
    return baseGreeting.replace('{{name}}', userName).trim();
  });

  onNewUser(): void {
    this.apiService.getUsableOrganizations().subscribe({
      next: (o) => {
        this.newUser = Reset.user();
        const localMap = new Map(Parameters.loggedUser.organizations.map(obj => [obj.organizationId, obj]));
        this.newUser.organizations = o.map(org => {
          // Controlla se esiste una versione aggiornata in locale
          if (localMap.has(org.organizationId)) {
            return { ...org, ...localMap.get(org.organizationId) }; 
            // Nota: usando lo spread operator {...objServer, ...locale} unisci le proprietà,
            // mantenendo quelle del server che non sono presenti nel locale.
          }
          return org; // Altrimenti restituisce l'oggetto originale del server
        });

        this.showDialogUser = true;
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onUserSaved(user: User) {
  }

  private loadData(): void {
    this.loading = true;

    this.apiService.getDashboardClinic(Parameters.loggedUser.id).subscribe({
      next: (data) => {
        this.dashboard = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }
}
